import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { createSelector, Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { filter, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';

import type { UserIdMap } from '@recruitee/account-data';
import { UserSelectors } from '@recruitee/account-data';
import { filterBoolean } from '@recruitee/common';
import { memoizeSelector } from '@recruitee/ngrx';
import {
  RequisitionAssignmentsSelectors,
  RequisitionsSelectors,
} from '@recruitee/requisitions-data';
import { RequisitionsConfigSelectors } from '@recruitee/requisitions-settings-data';
import type { RequisitionAssignment, Requisition } from '@recruitee/requisitions-types';

const isLoaded = memoizeSelector((id: number) =>
  createSelector(
    RequisitionsSelectors.isLoaded(id),
    RequisitionAssignmentsSelectors.isLoaded(id),
    (a, b) => a && b,
  ),
);

@Injectable()
export class RootPageDataService {
  public requisition$: Observable<Requisition>;
  public assignments$: Observable<RequisitionAssignment[]>;
  public adminsMap$: Observable<UserIdMap>;
  public isLoaded$: Observable<boolean>;
  public isFeatureEnabled$: Observable<boolean>;

  constructor(
    private route: ActivatedRoute,
    private store: Store,
  ) {
    const requisitionId$ = route.params.pipe(
      filter(params => !!params?.id),
      map(params => params.id),
    );
    this.requisition$ = requisitionId$.pipe(
      switchMap(id =>
        this.store.select(RequisitionsSelectors.byIdWithReferences(id)).pipe(filterBoolean),
      ),
    );
    this.assignments$ = requisitionId$.pipe(
      switchMap(id =>
        this.store
          .select(RequisitionAssignmentsSelectors.byId(id))
          .pipe(map(data => data?.assignments || [])),
      ),
    );
    this.adminsMap$ = store.select(UserSelectors.idMap);
    this.isLoaded$ = requisitionId$.pipe(switchMap(id => this.store.select(isLoaded(id))));
    this.isFeatureEnabled$ = this.store.select(
      RequisitionsConfigSelectors.requisitionsFeatureEnabled,
    );
  }
}
